import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { unitId, actionId } from '@/annotations'
import { Button } from '@/components/Button'
import { Icon } from '@/components/Icon'
import { Note } from '@/components/Note'
import { Chip } from '@/components/Chip'
import { Lozenge } from '@/components/Lozenge'
import { SaveBar } from '@/components/SaveBar'
import { getApiClient, unwrap, ApiProblemError } from '@/data/apiClient'
import { UnexpectedRefusal, isUnexpectedRefusal } from '@/screens/auth-shared/Refusals'
import { useActiveOrgId, useActiveWorkspaceId } from '@/data/store'
import type { components } from '@/api/generated/schema'
import type { Problem } from '@/data/apiClient'

type Organisation = components['schemas']['Organisation']
type Workspace = components['schemas']['Workspace']
type Schedule = components['schemas']['Schedule']
type ScheduleWindow = components['schemas']['ScheduleWindow']
type Retention = components['schemas']['Retention']

const WINDOWS: ScheduleWindow[] = ['00_02', '02_04', '04_06', '06_08', '08_10', '10_12', '12_14', '14_16', '16_18', '18_20', '20_22', '22_00']
const WINDOW_LABEL: Record<ScheduleWindow, string> = Object.fromEntries(WINDOWS.map((w) => [w, `${w.slice(0, 2)}:00–${w.slice(3)}:00`])) as Record<ScheduleWindow, string>

function retentionLabel(days: number): string {
  if (days === 365) return '1 year'
  if (days === 1095) return '3 years'
  return `${days} days`
}

/**
 * `v-setgeneral` — process/09-SCREEN-SPECS/v-setgeneral.md. Five independent panels, each with its
 * own `save-bar` — never a write on blur (`D90`, rule 8). **The spec's own "Data backups… whether
 * backups run" framing does not match any field on `WorkspaceUpdate`**, which carries exactly one
 * property, `auto_protect_new_repositories` — built against the schema, not the prose; reported.
 * **Retention's seven-year option has no entitlement field anywhere in `Retention` or
 * `Workspace`** — rendered permanently visible-and-disabled with a fixed reason, since there is no
 * served value to gate it on; reported rather than invented. **Its chip corrected from naming a
 * commercial plan ("Enterprise plan") to the same `on the roadmap` phrasing the two storage
 * options below already use** — `design/29-commercial-model.md`'s own words are "MVP issues no
 * tier at all," so naming a plan on a live control was asserting an entitlement boundary that
 * doesn't exist for any MVP customer; the fixed reason is a roadmap statement, same as the other
 * two, not a plan name.
 *
 * **`settings-panel-body`/`settings-panel-rail`/`row-actions`/`edit-link`/`lozenge`/`link` added
 * W168.** Every panel is now a real two-half layout: `settings-panel-body` (the working half — the
 * read-only summary, or the form while editing) beside `settings-panel-rail` (the explanatory
 * half). The four writable panels (Organisation, Data backups, Scheduled backup window, Retention
 * policy) start collapsed to a one-line read-only summary with a real `edit-link` (`row-actions`,
 * pencil + "Edit") that reveals the existing form; saving or cancelling collapses it back — this
 * does not change rule 8 (still never a write on blur, still commits only on its own `save-bar`),
 * it only changes when the form is shown. **Data storage provider gets an info icon in its own
 * `row-actions` instead of an `edit-link`** — matching the spec's own "an info icon where the panel
 * is not editable," since that panel has no save-bar at all (the choice is permanent). Two real
 * `lozenge`s wrap `schedule.timezone`/`schedule.next_window.display` in the schedule rail and the
 * live retention label in the retention rail — genuine served values, not decoration.
 *
 * **Only one of the spec's own five `link` instances is built**: "not-protected list," in Data
 * backups' own rail note, now really navigates to `v-notprotected` (`/notprotected`), the existing
 * screen that phrase already describes. **The other four are not built** — nothing else in this
 * spec's prose, rules, or operations names a destination for a link, and inventing four arbitrary
 * targets would be guessing rather than building; reported rather than invented.
 *
 * **Both "not built yet" chips recoloured from `off` to `type` (W172)** — the prototype's own
 * `v-setgeneral` markup draws "Bring your own storage · on the roadmap" and "Enterprise plan" both
 * as `.chip.type`, not `.chip.st-off`; `type` "names a kind rather than a state"
 * (`process/08-COMPONENT-CATALOGUE.md`), which is what both of these are.
 *
 * **The `org_id` `meta-block` given its own real border/`--gradient-card` background (W173)** — it
 * sits directly on the screen root, never nested inside another panel; see `OrderPane.tsx`'s own
 * doc comment for the prototype evidence and the nested-vs-top-level distinction this follows.
 */
export function VSetgeneral() {
  const orgId = useActiveOrgId()!
  const workspaceId = useActiveWorkspaceId()!
  const queryClient = useQueryClient()

  const org = useQuery({
    queryKey: ['organisation', orgId],
    // Found live, 2026-09-25 (systemic audit after VHealth.tsx/VDrift.tsx's own fix) — see that
    // fix's own doc comment for the mount-time/org-switch race this guards against.
    enabled: Boolean(orgId),
    queryFn: async (): Promise<Organisation> => {
      const client = getApiClient()
      const { data, error } = await client.GET('/orgs/{org_id}', { params: { path: { org_id: orgId } } })
      if (error) throw new ApiProblemError(error as Problem)
      return data as unknown as Organisation
    },
  })

  const workspace = useQuery({
    queryKey: ['workspace', orgId, workspaceId],
    enabled: Boolean(orgId && workspaceId),
    queryFn: async (): Promise<Workspace> => {
      const client = getApiClient()
      const { data, error } = await client.GET('/orgs/{org_id}/workspaces/{workspace_id}', { params: { path: { org_id: orgId, workspace_id: workspaceId } } })
      if (error) throw new ApiProblemError(error as Problem)
      return data as unknown as Workspace
    },
  })

  const schedule = useQuery({
    queryKey: ['schedule', orgId, workspaceId],
    enabled: Boolean(orgId && workspaceId),
    queryFn: async (): Promise<Schedule> => {
      const client = getApiClient()
      const { data, error } = await client.GET('/orgs/{org_id}/workspaces/{workspace_id}/schedule', { params: { path: { org_id: orgId, workspace_id: workspaceId } } })
      if (error) throw new ApiProblemError(error as Problem)
      return data as unknown as Schedule
    },
  })

  const retention = useQuery({
    queryKey: ['retention', orgId, workspaceId],
    enabled: Boolean(orgId && workspaceId),
    queryFn: async (): Promise<Retention> => {
      const client = getApiClient()
      const { data, error } = await client.GET('/orgs/{org_id}/workspaces/{workspace_id}/retention', { params: { path: { org_id: orgId, workspace_id: workspaceId } } })
      if (error) throw new ApiProblemError(error as Problem)
      return data as unknown as Retention
    },
  })

  return (
    <div data-scr={unitId('v-setgeneral')}>
      <h1 className="m-0 mb-8 text-section font-semibold">General settings</h1>

      <div className="grid gap-8" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(700px, 1fr))', alignItems: 'start' }}>
        {org.data && <OrganisationPanel org={org.data} orgId={orgId} onSaved={() => queryClient.invalidateQueries({ queryKey: ['organisation', orgId] })} />}
        {workspace.data && (
          <DataBackupsPanel workspace={workspace.data} orgId={orgId} workspaceId={workspaceId} onSaved={() => queryClient.invalidateQueries({ queryKey: ['workspace', orgId, workspaceId] })} />
        )}
        {schedule.data && (
          <SchedulePanel schedule={schedule.data} orgId={orgId} workspaceId={workspaceId} onSaved={() => queryClient.invalidateQueries({ queryKey: ['schedule', orgId, workspaceId] })} />
        )}
        {retention.data && (
          <RetentionPanel retention={retention.data} orgId={orgId} workspaceId={workspaceId} onSaved={() => queryClient.invalidateQueries({ queryKey: ['retention', orgId, workspaceId] })} />
        )}

        <div data-cmp="settings-panel" className="border border-bd rounded-2 p-6" style={{ background: 'var(--gradient-card)' }}>
          <div className="flex items-center gap-4 mb-4">
            <h3 className="m-0 text-body-lg font-semibold flex-1">Data storage provider</h3>
            <div data-cmp="row-actions" className="text-tx2">
              <span title="This choice is permanent and cannot be edited from here.">
                <Icon name="i-info" size="s" />
              </span>
            </div>
          </div>
          <div className="grid gap-6" style={{ gridTemplateColumns: '2fr 1fr' }}>
            <div data-cmp="settings-panel-body" className="flex flex-col gap-3 text-body-lg">
              <p>
                Your in-scope backup data is stored on <Lozenge variant="p">Spin-managed storage</Lozenge>{' '}
                at <Lozenge variant="b">{workspace.data?.region}</Lozenge> region.
              </p>
              <label className="flex items-center gap-3">
                <input type="radio" checked readOnly /> Spin-managed storage
              </label>
              <label className="flex items-center gap-3 text-muted">
                <input type="radio" disabled /> AWS S3 <Chip variant="type">Bring your own storage · on the roadmap</Chip>
              </label>
              <label className="flex items-center gap-3 text-muted">
                <input type="radio" disabled /> Azure Blob Storage <Chip variant="type">Bring your own storage · on the roadmap</Chip>
              </label>
              <Note tone="onPanel">
                Object Lock is enabled (no retention mode) — expiry only retires a manifest; objects are never deleted on a timer.
                Nothing is stored outside the region shown, and Bitbucket Cloud has no residency control, so source stays US-hosted
                regardless. <span className="underline">Learn more.</span>
              </Note>
            </div>
            <div data-cmp="settings-panel-rail" className="text-blue">
              <Note tone="onPanel">Chosen once at connection; permanent. Keys stay ours either way.</Note>
            </div>
          </div>
        </div>

        {schedule.data && <ConfigBackupPassPanel />}

        <DisconnectPanel orgId={orgId} workspaceId={workspaceId} />
      </div>

      <div data-cmp="meta-block" className="grid gap-3 mt-8 p-5 rounded-2 border border-bd" style={{ gridTemplateColumns: '160px 1fr', background: 'var(--gradient-card)' }}>
        <div data-cmp="kv-key" className="text-muted font-semibold text-body-lg">
          org_id
        </div>
        <div data-cmp="kv-value" className="font-mono text-muted text-body-lg">
          {orgId}
        </div>
      </div>
    </div>
  )
}

function PanelProblem({ problem }: { problem?: Problem }) {
  if (!problem) return null
  return isUnexpectedRefusal(problem.refusal_kind) ? <UnexpectedRefusal problem={problem} /> : <Note tone="onPanel">{problem.title}</Note>
}

function OrganisationPanel({ org, orgId, onSaved }: { org: Organisation; orgId: string; onSaved: () => void }) {
  const [name, setName] = useState(org.name)
  const [editing, setEditing] = useState(false)
  const save = useMutation({
    mutationFn: async () => {
      const client = getApiClient()
      const response = await client.PATCH('/orgs/{org_id}', { params: { path: { org_id: orgId } }, body: { name } })
      return unwrap(response as { data?: Organisation; error?: Problem })
    },
    onSuccess: () => {
      onSaved()
      setEditing(false)
    },
  })
  const problem = save.error instanceof ApiProblemError ? save.error.problem : undefined
  return (
    <div data-cmp="settings-panel" className="border border-bd rounded-2 p-6" style={{ background: 'var(--gradient-card)' }}>
      <div className="flex items-center gap-4 mb-4">
        <h3 className="m-0 text-body-lg font-semibold flex-1">Organisation</h3>
        <div data-cmp="row-actions" className="text-tx2">
          {!editing && (
            <button type="button" data-cmp="edit-link" className="flex items-center gap-2 text-small font-semibold text-link bg-transparent border-0 p-0 cursor-pointer" onClick={() => setEditing(true)}>
              <Icon name="i-pencil" size="s" /> Edit
            </button>
          )}
        </div>
      </div>
      <PanelProblem problem={problem} />
      <div className="grid gap-6" style={{ gridTemplateColumns: '2fr 1fr' }}>
        <div data-cmp="settings-panel-body">
          {editing ? (
            <>
              <div data-cmp="field">
                <label className="block mb-3 text-small font-semibold text-tx2">
                  Organisation name <i>*</i>
                  <input value={name} onChange={(e) => setName(e.target.value)} className="block w-full mt-2 border border-bd2 rounded-1 py-3 px-4 text-body-lg" />
                </label>
              </div>
              <SaveBar
                dataAct={actionId('organisation.update')}
                dirty={name !== org.name}
                pending={save.isPending}
                onSave={() => save.mutate()}
                onCancel={() => {
                  setName(org.name)
                  setEditing(false)
                }}
              />
            </>
          ) : (
            <div data-cmp="kv-value" className="text-body-lg">
              {org.name}
            </div>
          )}
        </div>
        <div data-cmp="settings-panel-rail" className="text-blue">
          <Note tone="onPanel">Access-control and billing boundary. Renaming only changes switcher/report labels — nothing about what's stored.</Note>
        </div>
      </div>
    </div>
  )
}

function DataBackupsPanel({ workspace, orgId, workspaceId, onSaved }: { workspace: Workspace; orgId: string; workspaceId: string; onSaved: () => void }) {
  const navigate = useNavigate()
  const [autoProtect, setAutoProtect] = useState(workspace.auto_protect_new_repositories ?? true)
  const [editing, setEditing] = useState(false)
  const save = useMutation({
    mutationFn: async () => {
      const client = getApiClient()
      const response = await client.PATCH('/orgs/{org_id}/workspaces/{workspace_id}', {
        params: { path: { org_id: orgId, workspace_id: workspaceId } },
        body: { auto_protect_new_repositories: autoProtect },
      })
      return unwrap(response as { data?: Workspace; error?: Problem })
    },
    onSuccess: () => {
      onSaved()
      setEditing(false)
    },
  })
  const problem = save.error instanceof ApiProblemError ? save.error.problem : undefined
  const servedAutoProtect = workspace.auto_protect_new_repositories ?? true
  return (
    <div data-cmp="settings-panel" className="border border-bd rounded-2 p-6" style={{ background: 'var(--gradient-card)' }}>
      <div className="flex items-center gap-4 mb-4">
        <h3 className="m-0 text-body-lg font-semibold flex-1">Data backups</h3>
        <div data-cmp="row-actions" className="text-tx2">
          {!editing && (
            <button type="button" data-cmp="edit-link" className="flex items-center gap-2 text-small font-semibold text-link bg-transparent border-0 p-0 cursor-pointer" onClick={() => setEditing(true)}>
              <Icon name="i-pencil" size="s" /> Edit
            </button>
          )}
        </div>
      </div>
      <PanelProblem problem={problem} />
      <div className="grid gap-6" style={{ gridTemplateColumns: '2fr 1fr' }}>
        <div data-cmp="settings-panel-body">
          {editing ? (
            <>
              <label className="flex items-center gap-4">
                <input type="checkbox" data-cmp="switch" checked={autoProtect} onChange={(e) => setAutoProtect(e.target.checked)} />
                Automatically protect new repositories
              </label>
              <SaveBar
                dataAct={actionId('workspace.update')}
                dirty={autoProtect !== servedAutoProtect}
                pending={save.isPending}
                onSave={() => save.mutate()}
                onCancel={() => {
                  setAutoProtect(servedAutoProtect)
                  setEditing(false)
                }}
              />
            </>
          ) : (
            <div data-cmp="kv-value" className="text-body-lg">
              Automatically protect new repositories: {servedAutoProtect ? 'On' : 'Off'}
            </div>
          )}
          <p className="mt-3 text-body">
            Backups enabled for{' '}
            {['Repositories & git history', 'Pull requests', 'Repository configuration', 'Workspace objects', 'Pipeline runs & deployment history', 'Downloads & snippets'].map((label) => (
              <Lozenge key={label} variant="g">
                {label}
              </Lozenge>
            ))}
          </p>
          <Note tone="onPanel">
            History is incremental; everything else is fully captured each snapshot (see{' '}
            <button type="button" data-cmp="link" className="underline text-link bg-transparent border-0 p-0 cursor-pointer" onClick={() => navigate('/backup')}>
              On-demand backup
            </button>
            ). Scope repositories on{' '}
            <button type="button" data-cmp="link" className="underline text-link bg-transparent border-0 p-0 cursor-pointer" onClick={() => navigate('/setrepos')}>
              Repository Inclusion and Exclusion
            </button>
            .
          </Note>
        </div>
        <div data-cmp="settings-panel-rail" className="text-blue">
          <Note tone="onPanel">
            Off? New repos land in the{' '}
            <button type="button" data-cmp="link" className="underline text-link bg-transparent border-0 p-0 cursor-pointer" onClick={() => navigate('/notprotected')}>
              not-protected list
            </button>{' '}
            with a count badge.
          </Note>
        </div>
      </div>
    </div>
  )
}

function SchedulePanel({ schedule, orgId, workspaceId, onSaved }: { schedule: Schedule; orgId: string; workspaceId: string; onSaved: () => void }) {
  const navigate = useNavigate()
  const [window, setWindowValue] = useState<ScheduleWindow>(schedule.window)
  const [enabled, setEnabled] = useState(schedule.enabled)
  const [editing, setEditing] = useState(false)
  const save = useMutation({
    mutationFn: async () => {
      const client = getApiClient()
      const response = await client.PUT('/orgs/{org_id}/workspaces/{workspace_id}/schedule', {
        params: { path: { org_id: orgId, workspace_id: workspaceId } },
        body: { window, timezone: schedule.timezone, enabled },
      })
      return unwrap(response as { data?: Schedule; error?: Problem })
    },
    onSuccess: () => {
      onSaved()
      setEditing(false)
    },
  })
  const problem = save.error instanceof ApiProblemError ? save.error.problem : undefined
  const dirty = window !== schedule.window || enabled !== schedule.enabled
  return (
    <div data-cmp="settings-panel" className="border border-bd rounded-2 p-6" style={{ background: 'var(--gradient-card)' }}>
      <div className="flex items-center gap-4 mb-4">
        <h3 className="m-0 text-body-lg font-semibold flex-1">Scheduled backup window</h3>
        <div data-cmp="row-actions" className="text-tx2">
          {!editing && (
            <button type="button" data-cmp="edit-link" className="flex items-center gap-2 text-small font-semibold text-link bg-transparent border-0 p-0 cursor-pointer" onClick={() => setEditing(true)}>
              <Icon name="i-pencil" size="s" /> Edit
            </button>
          )}
        </div>
      </div>
      <PanelProblem problem={problem} />
      <div className="grid gap-6" style={{ gridTemplateColumns: '2fr 1fr' }}>
        <div data-cmp="settings-panel-body">
          {editing ? (
            <>
              <label className="flex items-center gap-4 mb-4">
                <input type="checkbox" data-cmp="switch" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
                Scheduled captures run
              </label>
              <label className="block mb-3 text-small font-semibold text-tx2">
                Start backups between
                <select value={window} onChange={(e) => setWindowValue(e.target.value as ScheduleWindow)} className="block w-full mt-2 border border-bd2 rounded-1 py-3 px-4 text-body-lg">
                  {WINDOWS.map((w) => (
                    <option key={w} value={w}>
                      {WINDOW_LABEL[w]}
                    </option>
                  ))}
                </select>
              </label>
              <SaveBar
                dataAct={actionId('schedule.replace')}
                dirty={dirty}
                pending={save.isPending}
                onSave={() => save.mutate()}
                onCancel={() => {
                  setWindowValue(schedule.window)
                  setEnabled(schedule.enabled)
                  setEditing(false)
                }}
              />
            </>
          ) : (
            <div data-cmp="kv-value" className="text-body-lg">
              {schedule.enabled ? `Runs ${WINDOW_LABEL[schedule.window]}` : 'Scheduled captures are off'}
            </div>
          )}
          <Note tone="onPanel">
            Twelve two-hour windows (even hours, your timezone). Starts sometime within the window — never a promised minute. Per
            workspace; <b>schedules the data backup only</b> — the configuration backup runs separately, below.
          </Note>
          <p className="mt-2 text-right">
            <button type="button" data-cmp="link" className="underline text-link bg-transparent border-0 p-0 cursor-pointer" onClick={() => navigate('/backup')}>
              Back up now
            </button>
          </p>
        </div>
        <div data-cmp="settings-panel-rail" className="text-blue">
          <Note tone="onPanel">
            Honoured to the hour, never the minute — <Lozenge variant="b">{schedule.timezone}</Lozenge>.{' '}
            {schedule.next_window ? (
              <>
                Next window: <Lozenge variant="b">{schedule.next_window.display}</Lozenge>.
              </>
            ) : (
              ''
            )}
          </Note>
        </div>
      </div>
    </div>
  )
}

function RetentionPanel({ retention, orgId, workspaceId, onSaved }: { retention: Retention; orgId: string; workspaceId: string; onSaved: () => void }) {
  const [days, setDays] = useState(retention.retention_days)
  const [editing, setEditing] = useState(false)
  const save = useMutation({
    mutationFn: async () => {
      const client = getApiClient()
      const response = await client.PUT('/orgs/{org_id}/workspaces/{workspace_id}/retention', {
        params: { path: { org_id: orgId, workspace_id: workspaceId } },
        body: { retention_days: days },
      })
      return unwrap(response as { data?: Retention; error?: Problem })
    },
    onSuccess: () => {
      onSaved()
      setEditing(false)
    },
  })
  const problem = save.error instanceof ApiProblemError ? save.error.problem : undefined
  return (
    <div data-cmp="settings-panel" className="border border-bd rounded-2 p-6" style={{ background: 'var(--gradient-card)' }}>
      <div className="flex items-center gap-4 mb-4">
        <h3 className="m-0 text-body-lg font-semibold flex-1">Retention policy</h3>
        <div data-cmp="row-actions" className="text-tx2">
          {!editing && (
            <button type="button" data-cmp="edit-link" className="flex items-center gap-2 text-small font-semibold text-link bg-transparent border-0 p-0 cursor-pointer" onClick={() => setEditing(true)}>
              <Icon name="i-pencil" size="s" /> Edit
            </button>
          )}
        </div>
      </div>
      <PanelProblem problem={problem} />
      <div className="grid gap-6" style={{ gridTemplateColumns: '2fr 1fr' }}>
        <div data-cmp="settings-panel-body">
          {editing ? (
            <>
              <div data-cmp="radio-group" className="flex flex-col gap-3">
                <label className="flex items-center gap-3">
                  <input type="radio" name="retention" checked={days === 365} onChange={() => setDays(365)} /> 1 year
                </label>
                <label className="flex items-center gap-3">
                  <input type="radio" name="retention" checked={days === 1095} onChange={() => setDays(1095)} /> 3 years
                </label>
                <label className="flex items-center gap-3 text-muted">
                  <input type="radio" disabled /> 7 years <Chip variant="type">7-year retention · on the roadmap</Chip>
                </label>
              </div>
              <SaveBar
                dataAct={actionId('retention.replace')}
                dirty={days !== retention.retention_days}
                pending={save.isPending}
                onSave={() => save.mutate()}
                onCancel={() => {
                  setDays(retention.retention_days)
                  setEditing(false)
                }}
              />
            </>
          ) : (
            <div data-cmp="kv-value" className="text-body-lg">
              Your backups persist in storage for {retentionLabel(retention.retention_days)}, stamped onto each snapshot when it is
              created.
            </div>
          )}
          <Note tone="onPanel">
            <b>Applies to future snapshots only</b> — existing ones keep their original deadline, extendable but never shortened.
            Expired backups stop listing/restoring, but objects are never deleted — nothing else is lost.
          </Note>
        </div>
        <div data-cmp="settings-panel-rail" className="text-blue">
          <Note tone="onPanel">
            Currently <Lozenge variant="p">{retentionLabel(retention.retention_days)}</Lozenge> — can be extended, never shortened.
          </Note>
        </div>
      </div>
    </div>
  )
}

/** Prototype_v2.html `#v-setgeneral`'s fifth panel, "Configuration backup pass" — read-only (no
 * `save-bar`, matching the prototype's own missing one: the configuration pass runs on a fixed
 * daily schedule outside the data backup window, with nothing on this panel that is ever edited). */
function ConfigBackupPassPanel() {
  const navigate = useNavigate()
  return (
    <div data-cmp="settings-panel" className="border border-bd rounded-2 p-6" style={{ background: 'var(--gradient-card)' }}>
      <div className="flex items-center gap-4 mb-4">
        <h3 className="m-0 text-body-lg font-semibold flex-1">Configuration backup pass</h3>
        <div data-cmp="row-actions" className="text-tx2">
          <span title="This pass is not a setting and cannot be edited.">
            <Icon name="i-info" size="s" />
          </span>
        </div>
      </div>
      <div className="grid gap-6" style={{ gridTemplateColumns: '2fr 1fr' }}>
        <div data-cmp="settings-panel-body" className="flex flex-col gap-3">
          <p>
            Your workspace configuration is captured <Lozenge variant="p">daily</Lozenge>, in full, on a pass we run{' '}
            <Lozenge variant="b">outside your backup window</Lozenge>.
          </p>
          <Note tone="onPanel">
            Not a setting — captured in full daily, nothing skipped or pre-compared, on its own schedule so it never delays backups.
            Reports and drift comparisons cite the snapshot (and date) used; an unfinished pass reads <b>not re-observed</b>, never{' '}
            <b>no changes</b>.
          </Note>
          <p className="mt-2 text-right">
            <button type="button" data-cmp="link" className="underline text-link bg-transparent border-0 p-0 cursor-pointer" onClick={() => navigate('/drift')}>
              Compare two configuration snapshots
            </button>
          </p>
        </div>
        <div data-cmp="settings-panel-rail" className="text-blue" />
      </div>
    </div>
  )
}

function DisconnectPanel({ orgId, workspaceId }: { orgId: string; workspaceId: string }) {
  const workspace = useQuery({
    queryKey: ['workspace', orgId, workspaceId],
    // Found live, 2026-09-25 (systemic audit after VHealth.tsx/VDrift.tsx's own fix) — `orgId`/
    // `workspaceId` are props here, but the caller (`VSetgeneral`) forwards its own possibly-null
    // store values through a non-null assertion, so the same mount-time race reaches this far.
    enabled: Boolean(orgId && workspaceId),
    queryFn: async (): Promise<Workspace> => {
      const client = getApiClient()
      const { data, error } = await client.GET('/orgs/{org_id}/workspaces/{workspace_id}', { params: { path: { org_id: orgId, workspace_id: workspaceId } } })
      if (error) throw new ApiProblemError(error as Problem)
      return data as unknown as Workspace
    },
  })
  const disconnect = useMutation({
    mutationFn: async () => {
      const client = getApiClient()
      const { error } = await client.DELETE('/orgs/{org_id}/workspaces/{workspace_id}', { params: { path: { org_id: orgId, workspace_id: workspaceId } } })
      if (error) throw new ApiProblemError(error as Problem)
    },
  })
  const problem = disconnect.error instanceof ApiProblemError ? disconnect.error.problem : undefined
  return (
    <div data-cmp="settings-panel" className="border border-bd rounded-2 p-6" style={{ background: 'var(--gradient-card)' }}>
      <h3 className="m-0 mb-4 text-body-lg font-semibold">Disconnect this workspace</h3>
      <PanelProblem problem={problem} />
      <div className="grid gap-6" style={{ gridTemplateColumns: '2fr 1fr' }}>
        <div data-cmp="settings-panel-body" className="flex flex-col gap-3 text-body-lg">
          <p>
            Disconnecting stops every future backup for {workspace.data?.name} and removes the stored credential.
          </p>
          <Note tone="onPanel">
            <b>Nothing already backed up is deleted</b> — snapshots stay listed and restorable until retention expires; there's no way
            to delete data.
          </Note>
          <Button data-act={actionId('workspace.disconnect')} danger disabled={disconnect.isPending} onClick={() => disconnect.mutate()}>
            Disconnect workspace
          </Button>
        </div>
        <div data-cmp="settings-panel-rail" className="text-blue">
          <Note tone="onPanel">Your existing backups are untouched — see the details on the left.</Note>
        </div>
      </div>
    </div>
  )
}
